import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import LeafletMap from "../components/LeafletMap";
import { AsyncSelect, Button, toast } from "@coninsa/core";
import { safeAwait, storage } from "@coninsa/utils";
import NumberFormat from "react-number-format";

import {
  T_fieldSettings,
  getNuwweNeighborhood,
  createZohoLead,
  getNearBranchOffice,
  ZOHO_MIN_LEASING_VALUE,
  T_createNuwweClient_args,
  createNuwweClient,
} from "../api";

import { reverseGeocode, geocodeAddress } from "../geo";

type T_props = {
  onSuccess: (nextStep?: number) => void;
  onBack: () => void;
  fieldSettings: T_fieldSettings;
};

export type T_form = {
  cod_barrio: { value: string; label: string; city_id: string };
  cod_estrato: string;
  cod_sucursal: string;
  cod_tipo_inmueble: string;
  dir_inmueble: string;
  vlr_administracion: string;
  vlr_canon: string;

  apartment_number: string;
  building_co_ownership: string;
  coordinate_lat: string;
  coordinate_lon: string;
  independent_public_services: string;
  tower_number: string;
};

function Step2({ onSuccess, onBack, fieldSettings }: T_props) {
  const {
    control,
    formState: { isSubmitting, errors },
    handleSubmit,
    register,
    setValue,
  } = useForm<T_form>({
    defaultValues: async () => {
      return storage.get("consignation.property");
    },
  });

  const [markerCoords, setMarkerCoords] =
    useState<[lat: number, lon: number]>();

  const onSubmit = async (data: T_form) => {
    // Create or update client.
    const client = storage.get("consignation.client");
    const user = storage.get("consignation.user");

    const body: T_createNuwweClient_args = {
      cod_cliente: client.cod_cliente,
      primer_nombre: client.primer_nombre,
      segundo_nombre: client.segundo_nombre,
      primer_apellido: client.primer_apellido,
      segundo_apellido: client.segundo_apellido,
      email: client.email,
      cod_ciudad_cliente: data.cod_barrio?.city_id,
      cod_ciudad_documento: data.cod_barrio?.city_id,
      cod_barrio_cliente: data.cod_barrio.value,
      dir_cliente: data.dir_inmueble,
      cod_tipo_cliente: client.cod_tipo_cliente,
      cod_tipo_documento: client?.cod_tipo_documento?.value,
      tel_cliente: JSON.stringify([
        {
          id: "TE2",
          value: user.cellphone,
          obs: "",
        },
      ]),
    };

    const [error, response] = await safeAwait(createNuwweClient(body));

    if (error) {
      const cause = error?.cause ?? {}; // { email: [ 'foo', 'baz' ], abc: ['zoo'] }
      const arrErrors = Object.values(cause).reduce((acc, item) => {
        if (Array.isArray(item)) {
          acc = [...acc, ...item];
        }

        return acc;
      }, []); // [ ['foo', 'bazz'], ['zoo'] ]

      toast.error({
        title: error.message,
        message: (
          <div>
            {arrErrors.length > 0 && (
              <ul>
                {arrErrors.map((error) => (
                  <li>{error}</li>
                ))}
              </ul>
            )}
          </div>
        ),
      });

      return;
    }

    // Create property
    const [branchOfficeError, branchOffice] = await safeAwait(
      getNearBranchOffice(data.dir_inmueble)
    );

    if (branchOfficeError) {
      console.error("Error fetching branch office:", branchOfficeError);

      toast.error({
        title: "Algo está mal.",
        message: "Vuelva a intentarlo más tarde",
      });

      return;
    }

    const branchOfficeName = branchOffice?.sucursal?.sucursal ?? "ABR SABANETA";
    const branchOfficeCode = fieldSettings.property.branchOffices.find(
      (branch) => branch.label === branchOfficeName
    )?.value;

    if (branchOfficeCode) {
      data.cod_sucursal = branchOfficeCode;
    }

    // Override/format canon value
    const canon = parseInt(data.vlr_canon.toString().replaceAll(".", ""));
    const admValue = parseInt(
      data.vlr_administracion.toString().replaceAll(".", "")
    );
    storage.set("consignation.property", {
      ...data,
      vlr_canon: canon,
      vlr_administracion: admValue,
    });

    // Send information to Zoho.
    const cellphone = user.cellphone;
    const property = storage.get("consignation.property");

    const [zohoError] = await safeAwait(
      createZohoLead(cellphone, client, property, branchOfficeName)
    );

    if (zohoError) {
      console.error("Error creating Zoho lead:", zohoError);

      toast.error({
        title: "Algo está mal.",
        message: "Vuelva a intentarlo más tarde",
      });

      return;
    }

    // Check ZOHO condition to create nuwwe property
    if (
      canon > ZOHO_MIN_LEASING_VALUE &&
      Boolean(parseInt(data.independent_public_services))
    ) {
      onSuccess();
    } else {
      onSuccess(6);
    }
  };

  const handleChangeMarker = async (markerPos) => {
    const [lat, lon] = markerPos;
    setValue("coordinate_lat", lat);
    setValue("coordinate_lon", lon);
    // setMarkerCoords([lat, lon]);
  };

  const promiseNeighborhoodOptions = async (inputValue) => {
    const results = await getNuwweNeighborhood(inputValue);
    const options = results.map((item) => ({
      label: item.name,
      value: item.id,
      city_id: item.city_id,
    }));

    return options;
  };

  const handleDirChange = async (event) => {
    const coords = await geocodeAddress(event.target.value);

    if (coords) {
      setValue("coordinate_lat", coords[0].toString());
      setValue("coordinate_lon", coords[1].toString());
      setMarkerCoords(coords);
    }
  };

  return (
    <div className="co-card mx-auto max-w-4xl">
      <form className="co-form gap-y-7" onSubmit={handleSubmit(onSubmit)}>
        <div className="form-control">
          <label
            htmlFor="property_type"
            className="is-required mb-4 !font-bold"
          >
            Tipo de inmueble a consignar
          </label>
          <select
            {...register("cod_tipo_inmueble")}
            id="property_type"
            required
          >
            <option value="">- Seleccione una opción -</option>
            {fieldSettings?.property?.types?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-control">
          <label className="is-required">
            Valor mensual del arriendo, incluida la administración
          </label>
          <div className="form-input">
            <div className="input-prefix">COP$</div>
            <Controller
              control={control}
              name="vlr_canon"
              render={({ field }) => (
                <NumberFormat
                  thousandSeparator="."
                  decimalSeparator=","
                  required
                  {...field}
                />
              )}
            />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="form-control">
            <label htmlFor="stratum" className="is-required">
              Estrato
            </label>
            <select {...register("cod_estrato")} id="stratum" required>
              <option value="">- Seleccione una opción -</option>
              {fieldSettings?.property?.stratums?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <fieldset className="form-group are-radios">
            <legend className="is-required">
              ¿Cuentas con servicios públicos independientes?
            </legend>
            <div className="form-items">
              <div className="form-control is-radio">
                <input
                  {...register("independent_public_services")}
                  type="radio"
                  id="independent_public_services_true"
                  value="1"
                  required
                />
                <label htmlFor="independent_public_services_true">Sí</label>
              </div>
              <div className="form-control is-radio">
                <input
                  {...register("independent_public_services")}
                  type="radio"
                  id="independent_public_services_false"
                  value="0"
                  required
                />
                <label htmlFor="furnished_apartment_false">No</label>
              </div>
            </div>
          </fieldset>
        </div>

        <div className="form-control">
          <label className="is-required">
            Barrio donde está ubicado el inmueble
          </label>
          <Controller
            control={control}
            name="cod_barrio"
            render={({ field: { value, onChange } }) => (
              <AsyncSelect
                loadOptions={promiseNeighborhoodOptions}
                onChange={(option) => onChange(option)}
                value={value}
                placeholder="Nombre de barrio"
                noOptionsMessage={() => (
                  <span>Escribe el nombre del barrio</span>
                )}
                required
              />
            )}
          />
        </div>

        <div className="form-control">
          <label>Dirección del inmueble</label>
          <input
            {...register("dir_inmueble", {
              pattern: {
                value: /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ0-9\s#\-.]+,\s?[A-ZÁÉÍÓÚÜÑáéíóúüña-z\s]+$/,
                message:
                  "El formato debe ser: Calle o Carrera + número + coma + ciudad. Ej: Calle 17 sur #6 22, Mosquera",
              },
            })}
            type="text"
            id="address"
            placeholder="Escribe la dirección"
            required
            onBlur={handleDirChange}
          />
          {errors?.dir_inmueble && (
            <p className="form-error">{errors.dir_inmueble.message}</p>
          )}
          <p className="help-text mt-2 !text-sm text-gray-600">
            Cuéntanos dónde está ubicado tu inmueble con la dirección lo más
            clara posible. Así podemos ubicarlo bien en el mapa. Por ejemplo:
            Cra 13 # 93-55, Bogotá o Calle 10 Sur # 50-20, Medellín Evita poner
            cosas como "cerca al parque" o "por la iglesia", mejor usa el tipo
            de vía, número y ciudad.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="form-control">
            <label>Torre número</label>
            <input
              {...register("tower_number")}
              type="text"
              id="tower_number"
              placeholder="Ingresa el número de torre"
              required
            />
          </div>
          <div className="form-control">
            <label>Apartamento</label>
            <input
              {...register("apartment_number")}
              type="text"
              id="apartment_number"
              placeholder="Ingresa el número de apartamento"
              required
            />
          </div>
        </div>

        <div className="form-control">
          <label>Copropiedad</label>
          <input
            {...register("building_co_ownership")}
            type="text"
            id="building_co_ownership"
            placeholder="Ingresa el nombre de la copropiedad"
            required
          />
        </div>

        <div className="form-control">
          <label>Valor administración</label>
          <div className="form-input">
            <div className="input-prefix">COP$</div>
            <Controller
              control={control}
              name="vlr_administracion"
              render={({ field }) => (
                <NumberFormat
                  placeholder="Ingresa el valor de administración"
                  thousandSeparator="."
                  decimalSeparator=","
                  required
                  {...field}
                />
              )}
            />
          </div>
        </div>

        <div className="form-control is-map">
          <input
            {...register("coordinate_lat")}
            type="hidden"
            id="coordinate_lat"
            required
          />
          <input
            {...register("coordinate_lon")}
            type="hidden"
            id="coordinate_lon"
            required
          />
          <LeafletMap
            onChange={handleChangeMarker}
            coordinates={markerCoords}
          />
          <p className="help-text">
            Mueve el pin para determinar la ubicación exacta de tu inmueble
          </p>
        </div>

        <div className="flex justify-between gap-2 md:flex-row">
          <Button
            type="button"
            onClick={onBack}
            className="co-button is-secondary is-large has-fixed-icon"
          >
            <i className="co-icon icon-arrow"></i>
            Regresar
          </Button>

          <Button
            loading={isSubmitting}
            className="co-button is-progress-default is-large w-full md:w-auto"
          >
            Continuar
          </Button>
        </div>
      </form>
    </div>
  );
}

export default Step2;
